import { useState, useEffect } from 'react';
import { Users, Plus, Crown, Shield, User as UserIcon, Mail, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TeamManagement() {
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (currentTeam) {
      fetchTeamMembers(currentTeam.team_id);
    }
  }, [currentTeam]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams/my`, { withCredentials: true });
      setTeams(response.data);
      
      // Set current team
      const current = response.data.find(t => t.is_current);
      if (current) setCurrentTeam(current);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/members`, { withCredentials: true });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/teams`, { name: newTeamName }, { withCredentials: true });
      toast.success('Team created successfully!');
      setNewTeamName('');
      setShowCreateTeam(false);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const switchTeam = async (teamId) => {
    try {
      await axios.post(`${API}/teams/${teamId}/switch`, {}, { withCredentials: true });
      toast.success(teamId === 'personal' ? 'Switched to personal workspace' : 'Team switched');
      window.location.reload(); // Reload to update invoices
    } catch (error) {
      toast.error('Failed to switch team');
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    if (!currentTeam) {
      toast.error('Please select a team first');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/teams/${currentTeam.team_id}/invite`,
        { email: inviteEmail, role: inviteRole },
        { withCredentials: true }
      );
      toast.success('Member invited successfully!');
      setInviteEmail('');
      setShowInviteMember(false);
      fetchTeamMembers(currentTeam.team_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-amber-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-indigo-500" />;
      default:
        return <UserIcon className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Teams List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Your Teams</h3>
          <button
            onClick={() => setShowCreateTeam(!showCreateTeam)}
            className="px-4 py-2 bg-indigo-700 text-white hover:bg-indigo-800 transition-all font-medium rounded-lg flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Create Team</span>
          </button>
        </div>

        {showCreateTeam && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
          >
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="Team name"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-3"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCreateTeam(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={createTeam}
                disabled={isLoading}
                className="px-4 py-2 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </motion.div>
        )}

        <div className="space-y-2">
          {/* Personal Workspace */}
          <div
            onClick={() => switchTeam('personal')}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              !currentTeam || !currentTeam.team_id
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-200 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Personal Workspace</p>
                  <p className="text-xs text-slate-500">Your private invoices</p>
                </div>
              </div>
              {(!currentTeam || !currentTeam.team_id) && (
                <CheckCircle className="w-5 h-5 text-indigo-700" />
              )}
            </div>
          </div>

          {/* Teams */}
          {teams.map((team) => (
            <div
              key={team.team_id}
              onClick={() => {
                switchTeam(team.team_id);
                setCurrentTeam(team);
              }}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                team.is_current
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-slate-900">{team.name}</p>
                      {getRoleIcon(team.user_role)}
                    </div>
                    <p className="text-xs text-slate-500">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                {team.is_current && (
                  <CheckCircle className="w-5 h-5 text-indigo-700" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Members */}
      {currentTeam && currentTeam.team_id && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Team Members</h3>
            {(currentTeam.user_role === 'owner' || currentTeam.user_role === 'admin') && (
              <button
                onClick={() => setShowInviteMember(!showInviteMember)}
                className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 transition-all font-medium rounded-lg flex items-center space-x-2 text-sm"
              >
                <Mail className="w-4 h-4" />
                <span>Invite Member</span>
              </button>
            )}
          </div>

          {showInviteMember && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
            >
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Email address"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-3"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 mb-3"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowInviteMember(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  onClick={inviteMember}
                  disabled={isLoading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  Invite
                </button>
              </div>
            </motion.div>
          )}

          <div className="space-y-2">
            {teamMembers.map((member) => (
              <div key={member.user_id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {member.user.picture ? (
                      <img
                        src={member.user.picture}
                        alt={member.user.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-indigo-700">
                          {member.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-900">{member.user.name}</p>
                      <p className="text-xs text-slate-500">{member.user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getRoleIcon(member.role)}
                    <span className="text-xs text-slate-600 capitalize">{member.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
